/**
 * ─────────────────────────────────────────────────────────────────
 * Modal para guardar la selección del comparador en una lista.
 *
 * Comportamiento por plataforma:
 *  - Móvil: Bottom sheet (desliza desde abajo) — patrón nativo
 *  - Escritorio: Dialog centrado estándar
 * ─────────────────────────────────────────────────────────────────
 */
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    MenuItem,
    TextField,
    Stack,
    Typography,
    Box,
    Divider,
    Slide,
    useTheme,
    useMediaQuery,
} from "@mui/material";
import { useEffect, useState } from "react";
import { obtenerListasDelUsuario } from "../api/listas";

/** Transición de deslizamiento desde abajo para el bottom sheet móvil */
const SlideUp = (props) => <Slide direction="up" {...props} />;

export default function GuardarEnListaModal({ open, onClose, onConfirm }) {
    const theme    = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    const [listas,           setListas]           = useState([]);
    const [listaSeleccionada, setListaSeleccionada] = useState("");

    /** Carga las listas del usuario cuando se abre el modal */
    useEffect(() => {
        if (!open) return;
        obtenerListasDelUsuario()
            .then((data) => setListas(Array.isArray(data) ? data : []))
            .catch(() => setListas([]));
    }, [open]);

    /** Limpia la selección al cerrar */
    const handleClose = () => {
        setListaSeleccionada("");
        onClose();
    };

    /** Confirma la selección y cierra */
    function handleConfirm() {
        if (!listaSeleccionada) return;
        onConfirm(listaSeleccionada);
        setListaSeleccionada("");
    }

    /** Selector de lista — compartido entre móvil y escritorio */
    const selector = (
        <TextField
            select
            label="Lista"
            value={listaSeleccionada}
            onChange={(e) => setListaSeleccionada(e.target.value)}
            fullWidth
            size={isMobile ? "medium" : "medium"}
            SelectProps={{
                MenuProps: {
                    PaperProps: {
                        sx: { maxHeight: 240, borderRadius: 3 },
                    },
                },
            }}
        >
            {listas.map((l) => (
                <MenuItem key={l.idlista} value={l.idlista}>
                    {l.nombre}
                </MenuItem>
            ))}
        </TextField>
    );

    /* ── MÓVIL: Bottom Sheet ─────────────────────────────────────── */
    if (isMobile) {
        return (
            <Dialog
                open={open}
                onClose={handleClose}
                TransitionComponent={SlideUp}
                fullWidth
                PaperProps={{
                    sx: {
                        position:     "fixed",
                        bottom:       0,
                        left:         0,
                        right:        0,
                        m:            0,
                        width:        "100%",
                        maxWidth:     "100%",
                        borderRadius: "20px 20px 0 0",
                        pb:           "env(safe-area-inset-bottom)",
                        maxHeight:    "75vh",
                        overflow:     "auto",
                    },
                }}
                sx={{
                    "& .MuiDialog-container": { alignItems: "flex-end" },
                    // Bloquea el scroll del fondo cuando el modal está abierto
                    "& .MuiBackdrop-root": { backdropFilter: "blur(2px)" },
                }}
                // Scroll lock garantiza que la página no se desplace
                disableScrollLock={false}
                keepMounted={false}
            >
                {/* Pastilla de arrastre */}
                <Box sx={{ display: "flex", justifyContent: "center", pt: 1.5, pb: 0.5 }}>
                    <Box sx={{ width: 40, height: 4, borderRadius: 99, bgcolor: "divider" }} />
                </Box>

                <Box sx={{ px: 2.5, pt: 1, pb: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 800 }}>
                        💾 Guardar selección
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.6 }}>
                        Elige la lista donde quieres guardar los productos
                    </Typography>
                </Box>

                <Divider />

                <Box sx={{ px: 2.5, pt: 2.5, pb: 2 }}>
                    {selector}
                </Box>

                {/* Botones full-width */}
                <Box sx={{ px: 2.5, pb: 2, display: "flex", gap: 1.5 }}>
                    <Button
                        fullWidth
                        variant="outlined"
                        onClick={handleClose}
                        sx={{
                            borderRadius:  2,
                            fontWeight:    700,
                            textTransform: "none",
                            minHeight:     48,
                        }}
                    >
                        Cancelar
                    </Button>
                    <Button
                        fullWidth
                        variant="contained"
                        onClick={handleConfirm}
                        disabled={!listaSeleccionada}
                        sx={{
                            borderRadius:  2,
                            fontWeight:    700,
                            textTransform: "none",
                            minHeight:     48,
                        }}
                    >
                        Guardar
                    </Button>
                </Box>
            </Dialog>
        );
    }

    /* ── ESCRITORIO: Dialog centrado estándar ────────────────────── */
    return (
        <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
            <DialogTitle>
                <Typography fontWeight={700}>💾 Guardar selección</Typography>
            </DialogTitle>

            <DialogContent>
                <Stack spacing={2} mt={1}>
                    <Typography color="text.secondary">
                        Elige la lista donde quieres guardar los productos.
                    </Typography>
                    {selector}
                </Stack>
            </DialogContent>

            <DialogActions sx={{ p: 2 }}>
                <Button onClick={handleClose} sx={{ textTransform: "none" }}>
                    Cancelar
                </Button>
                <Button
                    variant="contained"
                    onClick={handleConfirm}
                    disabled={!listaSeleccionada}
                    sx={{ textTransform: "none", fontWeight: 700 }}
                >
                    Guardar
                </Button>
            </DialogActions>
        </Dialog>
    );
}